var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { hexToRgb } from "../utils/colors.js";
import sharedFetch from "../utils/sharedFetch.js";
import ImageLoader from "./ImageLoader.js";
import noise from "../vendor/noise/index.js";
import * as url from "url";
import { a as assignGlobalSingleton } from "../global-33d5eebf.js";
import "pixi.js";
const rotationMasks = {
  diagonal: 536870912,
  vertical: 1073741824,
  horizontal: 2147483648
};
const _MapLoader = class _MapLoader {
  constructor() {
    __publicField(this, "maps", {});
    __publicField(this, "tilesetPromises", {});
    __publicField(this, "tilesets", {});
    __publicField(this, "mapTiles", {});
    __publicField(this, "canvas", document.createElement("canvas"));
    __publicField(this, "mapFloors", {});
    __publicField(this, "skins", {});
  }
  static getInstance() {
    return assignGlobalSingleton("MapLoader", () => new _MapLoader());
  }
  get(name) {
    return this.maps[name];
  }
  getSkin(name) {
    return {
      floor: this.skins[name].floor,
      tiles: Object.entries(this.mapTiles).reduce((acc, [key, value]) => {
        if (key.startsWith(name)) {
          acc[key] = value;
        }
        return acc;
      }, {})
    };
  }
  getFloor(name) {
    return {
      floors: this.mapFloors[name],
      ...this.skins[name].floor
    };
  }
  async loadMap(name, url2) {
    if (this.maps[name]) {
      return this.maps[name];
    }
    const mapData = await this.loadMapData(url2, name);
    this.maps[name] = mapData;
    return mapData;
  }
  async loadMapData(url2, name) {
    const response = await sharedFetch(url2);
    const rootPath = url2.split("/").slice(0, -1).join("/") + "/";
    const mapJson = await response.json();
    const tileLayer = mapJson.layers.find((layer) => layer.type === "tilelayer");
    const customTiles = mapJson.layers.find((tileset) => tileset.name === "custom-tiles");
    const map = await this.parseMap(mapJson, tileLayer, customTiles, rootPath, name);
    const triggers = await this.parseTriggers(mapJson, rootPath, map);
    return {
      source: mapJson,
      triggers,
      map
    };
  }
  async parseMap(mapJson, json, customTiles, urlFolder, name) {
    const tiles = {};
    if (mapJson.orientation === "isometric") {
      const GameTile = await this.parseWall(mapJson, urlFolder);
      this.mapTiles[name] = GameTile;
      tiles[0] = {
        name,
        width: mapJson.width / 20,
        height: mapJson.height / 20
      };
      json.data = json.data.map(() => {
        return 0;
      });
    } else {
      if (customTiles) {
        for (const object of customTiles.objects) {
          if (!object.visible) {
            continue;
          }
          const x = Math.floor(object.x / mapJson.tilewidth);
          const y = Math.floor(object.y / mapJson.tileheight);
          const index = x + y * json.width;
          let width = Math.ceil(object.width / mapJson.tilewidth);
          let height = Math.ceil(object.height / mapJson.tileheight);
          if (Math.round(object.x / mapJson.tilewidth) > Math.floor(object.x / mapJson.tilewidth)) {
            width += 1;
          }
          if (Math.round(object.y / mapJson.tileheight) > Math.floor(object.y / mapJson.tileheight)) {
            height += 1;
          }
          tiles[index] = {
            name: object.name,
            width,
            height
          };
        }
      }
    }
    return {
      data: json.data,
      height: json.height,
      width: json.width,
      customTiles: tiles
    };
  }
  async parseTriggers(json, rootPath, map) {
    const data = JSON.parse(JSON.stringify(json));
    const triggers = data.layers.find((layer) => layer.name === "triggers");
    if (triggers) {
      triggers.objects.forEach((object) => {
        if (json.orientation === "isometric") {
          const mapPosition = {
            x: (object.x - object.width / 2) / 640,
            y: (object.y - object.height / 2) / 640
          };
          object.x = mapPosition.x;
          object.y = mapPosition.y;
        } else {
          const mapPosition = {
            x: (object.x + object.width / 2) / json.tilewidth,
            y: (object.y + object.height / 2) / json.tileheight
          };
          object.x = mapPosition.x;
          object.y = mapPosition.y;
        }
      });
      const triggerPromises = triggers.objects.map(async (trigger) => {
        var _a, _b, _c, _d, _e;
        if ((!trigger.properties || trigger.properties.length === 0) && trigger.gid === void 0) {
          return null;
        }
        const properties = trigger.properties ? await Promise.all(
          trigger.properties.map((prop) => {
            if (prop.name === "fromFile") {
              return sharedFetch(rootPath + prop.value).then((response) => response.json()).then((json2) => ({
                name: prop.name,
                value: json2
              }));
            }
            return prop;
          })
        ) : [];
        const propertyFlatMap = (properties == null ? void 0 : properties.reduce((acc, prop) => {
          if (prop.name === "fromFile") {
            return { ...prop.value, ...acc };
          }
          acc[prop.name] = prop.value;
          return acc;
        }, {})) ?? {};
        const forcedComponents = [];
        const propertyMap = Object.entries(propertyFlatMap).reduce((acc, prop) => {
          const [key, value] = prop;
          if (key.includes(".")) {
            const keys = key.split(".");
            let current = acc;
            for (let i = 0; i < keys.length; i++) {
              const key2 = keys[i];
              if (i === keys.length - 1) {
                current[key2] = value;
              } else {
                if (!current[key2]) {
                  current[key2] = {};
                }
                current = current[key2];
              }
            }
          } else {
            if (key[0] === key[0].toUpperCase() && typeof value === "boolean") {
              if (value === true) {
                forcedComponents.push(key);
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {});
        if (propertyMap.disabled === true || !trigger.visible) {
          return null;
        }
        let triggerType = ((_a = propertyMap.trigger) == null ? void 0 : _a.event) ?? "UNKNOWN";
        let width = trigger.width / json.tilewidth;
        let height = trigger.height / json.tileheight;
        if (triggerType === "UNKNOWN" && trigger.gid !== void 0) {
          triggerType = "MAPENTITY";
          width = trigger.width;
          height = trigger.height;
          const tiledImage = this.getTiledImageFromGid(trigger.gid, rootPath, json);
          forcedComponents.push("MapSprite");
          propertyMap.MapSprite = {
            name: (_b = tiledImage.tileImage) == null ? void 0 : _b.name,
            flipHorizontal: tiledImage.horizontal,
            flipVertical: tiledImage.vertical,
            visible: ((_c = propertyMap.MapSprite) == null ? void 0 : _c.visible) ?? true
          };
        }
        const components = [];
        if (forcedComponents.length) {
          forcedComponents.forEach((key) => {
            if (!propertyMap[key]) {
              components.push({ type: key });
            } else {
              components.push({ type: key, ...propertyMap[key] });
            }
          });
        }
        return {
          type: triggerType,
          name: ((_d = propertyMap.trigger) == null ? void 0 : _d.name) ?? trigger.name,
          condition: ((_e = propertyMap.trigger) == null ? void 0 : _e.condition) ?? {},
          components,
          x: trigger.x,
          y: trigger.y,
          width,
          height,
          properties: propertyMap
        };
      });
      return (await Promise.all(triggerPromises)).filter((trigger) => trigger !== null);
    }
    return [];
  }
  async loadSkin(name, url2) {
    const response = await sharedFetch(url2);
    const mapJson = await response.json();
    const urlFolder = url2.substring(0, url2.lastIndexOf("/") + 1);
    const wallPromises = [];
    for (const wall of mapJson.walls) {
      const wallUrl = `${urlFolder}/${wall}.json`;
      wallPromises.push(this.loadMapTile(wall, wallUrl));
    }
    await Promise.all(wallPromises);
    const floors = await this.parseFloor(mapJson.name, mapJson.floor, urlFolder);
    this.mapFloors[name] = floors;
    this.skins[name] = mapJson;
  }
  async loadMapTile(name, url2) {
    const response = await sharedFetch(url2);
    const mapJson = await response.json();
    const urlFolder = url2.substring(0, url2.lastIndexOf("/"));
    const GameTile = await this.parseWall(mapJson, urlFolder);
    this.mapTiles[name] = GameTile;
  }
  async parseWall(json, urlPath) {
    const wall = {
      tilesets: [],
      layers: [],
      imageData: null,
      center: { x: 0, y: 0 },
      padding: { x: 0, y: 0 },
      sprites: []
    };
    const tileSets = await Promise.all(json.tilesets.map((tileset) => this.loadTileset(tileset, urlPath)));
    wall.tilesets = tileSets;
    for (const layer of json.layers) {
      if (layer.type === "objectgroup") {
        for (const object of layer.objects) {
          if (object.polygon) {
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;
            for (const point of object.polygon) {
              minX = Math.min(minX, point.x);
              minY = Math.min(minY, point.y);
              maxX = Math.max(maxX, point.x);
              maxY = Math.max(maxY, point.y);
            }
            object.width = maxX - minX;
            object.height = maxY - minY;
          }
        }
      }
      wall.layers.push(layer);
    }
    wall.layers.forEach((layer) => {
      if (layer.type !== "objectgroup")
        this.parseSprites(json, urlPath, layer, wall.tilesets, wall.sprites);
    });
    return wall;
  }
  getTiledImageFromGid(gid, urlPath, map) {
    var _a;
    const horizontalFlag = !!(gid & rotationMasks.horizontal);
    const verticalFlag = !!(gid & rotationMasks.vertical);
    let tile = gid & ~(rotationMasks.diagonal | rotationMasks.horizontal | rotationMasks.vertical);
    const source = (_a = map.tilesets.find((tileset2, index) => {
      var _a2;
      const nextFirstGuid = ((_a2 = map.tilesets[index + 1]) == null ? void 0 : _a2.firstgid) ?? Infinity;
      if (tile < nextFirstGuid) {
        tile -= tileset2.firstgid;
        return true;
      }
    })) == null ? void 0 : _a.source;
    if (!source) {
      return {
        tileImage: null,
        horizontal: horizontalFlag,
        vertical: verticalFlag,
        tileset: null
      };
    }
    const path = url.resolve(urlPath, source);
    const tileset = this.tilesets[path];
    if (!tileset) {
      return {
        tileImage: null,
        horizontal: horizontalFlag,
        vertical: verticalFlag,
        tileset: null
      };
    }
    const tileImage = tileset.images[tile];
    if (!tileImage) {
      throw new Error("Tile not found");
    }
    return {
      tileImage,
      horizontal: horizontalFlag,
      vertical: verticalFlag,
      tileset
    };
  }
  async loadTileset(tileset, urlPath) {
    const path = url.resolve(urlPath, tileset.source);
    if (this.tilesetPromises[path] !== void 0) {
      return this.tilesetPromises[path];
    }
    this.tilesetPromises[path] = this.parseTileset(tileset, urlPath);
    return this.tilesetPromises[path];
  }
  async parseTileset(tileset, urlPath) {
    const isometricTileset = {
      firstgid: tileset.firstgid,
      images: {}
    };
    if (!tileset.source) {
      throw new Error("Tileset source is missing");
    }
    const sourceDirectory = tileset.source.split("/").slice(0, -1).join("/") + "/";
    const path = url.resolve(urlPath, tileset.source);
    const source = await sharedFetch(path);
    const json = await source.json();
    if (json.tiles) {
      isometricTileset.type = "imagegroup";
      await Promise.all(
        json.tiles.map(async (tile) => {
          var _a;
          let imageSrc = tile.image;
          if (imageSrc.startsWith("./")) {
            imageSrc = imageSrc.substring(2);
          }
          const imagePath = `${urlPath}${sourceDirectory}${imageSrc}`;
          const name = `${sourceDirectory}${imageSrc}`.replaceAll("/", "__").replaceAll(".", "_");
          const image = await ImageLoader.getInstance().loadImage(name, imagePath, { skipPixi: false });
          const rawZindexes = ((_a = tile.objectgroup) == null ? void 0 : _a.objects.sort(({ x: xa }, { x: xb }) => xa - xb)) ?? [];
          const zIndexes = [];
          let x = 0;
          if (rawZindexes.length === 1) {
            zIndexes[0] = { x: 0, y: rawZindexes[0].y, width: tile.imagewidth };
          } else if (rawZindexes.length > 1) {
            for (let i = 0; i < rawZindexes.length - 1; i++) {
              const zIndex = rawZindexes[i];
              const nextZIndex = rawZindexes[i + 1];
              const width = Math.floor(nextZIndex.x) - x;
              zIndexes.push({
                x,
                y: zIndex.y,
                width
              });
              x += width;
            }
            zIndexes.push({
              x,
              y: rawZindexes[rawZindexes.length - 1].y,
              width: tile.imagewidth - x
            });
          }
          for (let i = 0; i < rawZindexes.length; i++) {
            const zIndexObject = rawZindexes[i];
            rawZindexes[i] = {
              x: Math.floor(x),
              y: zIndexObject.y,
              width: zIndexObject.width
            };
          }
          const tileImage = {
            image,
            name,
            width: tile.imagewidth,
            height: tile.imageheight,
            zIndexes
          };
          isometricTileset.images[tile.id] = tileImage;
        })
      );
    } else {
      let imageSrc = json.image;
      if (imageSrc.startsWith("./")) {
        imageSrc = imageSrc.substring(2);
      }
      isometricTileset.type = "tileset";
      const imagePath = `${urlPath}${sourceDirectory}${imageSrc}`;
      const name = `${sourceDirectory}${imageSrc}`.replaceAll("/", "__").split(".").shift();
      const image = await ImageLoader.getInstance().loadImage(name, imagePath, { skipPixi: false });
      const tileImage = {
        image,
        name,
        width: json.tilewidth,
        height: json.tileheight,
        zIndexes: []
      };
      isometricTileset.images[0] = tileImage;
    }
    this.tilesets[path] = isometricTileset;
    return isometricTileset;
  }
  parseSprites(json, urlPath, layer, tilesets, spriteList) {
    if (layer.name === "wall") {
      return;
    }
    const x_start = 20 / 2 * json.tilewidth;
    const y_start = 20 / 2 * json.tileheight;
    for (let y = 0; y < layer.height; y++) {
      for (let x = 0; x < layer.width; x++) {
        const tile = layer.data[y * layer.width + x];
        if (tile === 0)
          continue;
        const { horizontal, vertical, tileset, tileImage } = this.getTiledImageFromGid(tile, urlPath, json);
        if (!tileset)
          continue;
        if (!tileImage)
          continue;
        const screenX = x_start + (x - y) * json.tilewidth / 2;
        const screenY = y_start + (x + y) * json.tileheight / 2 - tileImage.image.image.height;
        const sprite = {
          image: tileImage.image.image,
          name: tileImage.name,
          x: screenX,
          y: screenY,
          width: tileImage.image.image.width,
          height: tileImage.image.image.height,
          hFlip: horizontal,
          vFlip: vertical,
          zIndexes: tileImage.zIndexes
        };
        spriteList.push(sprite);
      }
    }
  }
  loadImage(url2) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        resolve(image);
      };
      image.onerror = (err) => {
        reject(err);
      };
      image.src = url2;
    });
  }
  async parseFloor(name, { stamp, density, stampColor }, urlFolder) {
    const imagePath = `${urlFolder}${stamp}`;
    const image = await this.loadImage(imagePath);
    const floors = [];
    const floorPromises = [];
    for (let i = 0; i < 5; i++) {
      const [imageData] = this.generateFloor({
        stamp: image,
        seed: Math.random() * 100,
        density,
        stampColor
      });
      floors.push({
        imageData,
        center: { x: 0, y: 0 }
      });
      floorPromises.push(ImageLoader.getInstance().loadImage(`${name}_floor_${i}`, imageData == null ? void 0 : imageData.src));
    }
    await Promise.all(floorPromises);
    return floors;
  }
  generateFloor({
    stamp,
    seed,
    density,
    stampColor
  }) {
    this.canvas.width = 968;
    this.canvas.height = 516;
    const rgb = hexToRgb(stampColor);
    const ctx = this.canvas.getContext("2d");
    let dx = 0, dy = 0;
    ctx.save();
    ctx.translate(484, 32);
    ctx.scale(1, 0.5);
    ctx.rotate(45 * Math.PI / 180);
    const count = 10 * density;
    const size = 64 / density;
    const positions = [];
    for (let y = 0; y < count; y++) {
      for (let x = 0; x < count; x++) {
        ctx.strokeStyle = "#FFF";
        positions.push({ x: dx, y: dy });
        noise.simplex2(dx / 180 + seed * 3, dy / 180 + seed * 2);
        const cx = dx + Math.random() * size, cy = dy + Math.random() * size, innerRadius = size * Math.random() * 0.25, outerRadius = size * (Math.random() * 0.5 + 0.5);
        const gradient = ctx.createRadialGradient(cx, cy, innerRadius, cx, cy, outerRadius);
        gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.65)`);
        gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(cx, cy, outerRadius, 0, 2 * Math.PI);
        ctx.fill();
        dx += size;
      }
      dx = 0;
      dy += size;
    }
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }
    ctx.globalAlpha = 0.25;
    positions.forEach((pos) => {
      ({ x: dx, y: dy } = pos);
      let out = noise.simplex2(dx / 180, dy / 180);
      const rotation = noise.simplex2(dx, dy) * 360;
      out = out * 0.25 + 0.75;
      ctx.fillStyle = `rgb(${125 + out * 130}, ${125 + out * 130}, ${125 + out * 130})`;
      ctx.translate(dx + size / 2, dy + size / 2);
      ctx.rotate(rotation * Math.PI / 180);
      ctx.drawImage(stamp, -size / 2, -size / 2, size * 2 * out, size * 2 * out);
      ctx.rotate(-rotation * Math.PI / 180);
      ctx.translate(-dx - size / 2, -dy - size / 2);
    });
    ctx.restore();
    const image = new Image();
    image.src = this.canvas.toDataURL();
    return [image, this.canvas.width, this.canvas.height];
  }
};
__publicField(_MapLoader, "_instance");
let MapLoader = _MapLoader;
export {
  MapLoader as default
};
//# sourceMappingURL=MapLoader.js.map
